import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { adminApi } from '../services/adminApi';
import './AdminOrders.css';
import { proxyBucketUrl, handleImageError } from '../utils/imageUtils';

interface Order {
  id: number;
  orderNumber: string;
  userId: number;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
  items?: OrderItem[];
  orderItems?: OrderItem[];
  totalAmount: number;
  status: 'PENDING' | 'CONFIRMED' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED';
  paymentStatus: 'pending' | 'paid' | 'failed';
  createdAt: string;
  updatedAt: string;
  shippingAddress?: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
}

interface OrderItem {
  id: number;
  productId: number;
  quantity: number;
  price: number;
  product: {
    name: string;
    images: string[];
  };
}

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  // Helper function to get order items regardless of API structure
  const getOrderItems = (order: Order): OrderItem[] => {
    return order.items || order.orderItems || [];
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getOrders();
      
      // Handle different API response formats
      let ordersArray: Order[] = [];
      if (Array.isArray(data)) {
        ordersArray = data;
      } else if (data && Array.isArray(data.orders)) {
        ordersArray = data.orders;
      } else if (data && Array.isArray(data.data)) {
        ordersArray = data.data;
      } else {
        ordersArray = [];
      }
      
      setOrders(ordersArray);
    } catch (err: any) {
      setError(err.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId: number, newStatus: string) => {
    try {
      await adminApi.updateOrderStatus(orderId, newStatus);
      await loadOrders();
      setError(''); // Clear any previous errors
    } catch (err: any) {
      setError(err.message || 'Failed to update order status');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PENDING': return 'status-pending';
      case 'CONFIRMED': return 'status-confirmed';
      case 'PROCESSING': return 'status-processing';
      case 'SHIPPED': return 'status-shipped';
      case 'DELIVERED': return 'status-delivered';
      case 'CANCELLED': return 'status-cancelled';
      default: return 'status-pending';
    }
  };

  const getPaymentStatusClass = (status: string) => {
    switch (status) {
      case 'paid': return 'payment-paid';
      case 'pending': return 'payment-pending';
      case 'failed': return 'payment-failed';
      default: return 'payment-pending';
    }
  };

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(order => order.status === statusFilter);

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handleDeleteOrder = async (orderId: number) => {
    if (window.confirm('Are you sure you want to delete this order? This cannot be undone.')) {
      try {
        await adminApi.deleteOrder(orderId);
        await loadOrders();
        setError('');
      } catch (err: any) {
        setError(err.message || 'Failed to delete order');
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Loading Orders...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-orders">
        <div className="orders-header">
          <div className="header-content">
            <h1>Orders Management</h1>
            <p>Manage customer orders and shipping</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={loadOrders}>
              <span className="btn-icon">🔄</span>
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V13M12 17.0195V17M10.29 3.86L1.82 18C1.64468 18.3024 1.55274 18.6453 1.55376 18.9945C1.55478 19.3437 1.64868 19.6861 1.82601 19.9877C2.00333 20.2893 2.25952 20.5368 2.56983 20.7039C2.88014 20.871 3.22929 20.9519 3.58 20.94H20.42C20.7707 20.9519 21.1199 20.871 21.4302 20.7039C21.7405 20.5368 21.9967 20.2893 22.174 19.9877C22.3513 19.6861 22.4452 19.3437 22.4462 18.9945C22.4473 18.6453 22.3553 18.3024 22.18 18L13.71 3.86C13.5318 3.56611 13.2807 3.32312 12.9812 3.15448C12.6817 2.98585 12.3438 2.89725 12 2.89725C11.6562 2.89725 11.3183 2.98585 11.0188 3.15448C10.7193 3.32312 10.4682 3.56611 10.29 3.86V3.86Z" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            {error}
            <button onClick={() => setError('')} className="close-error">×</button>
          </div>
        )}

        <div className="orders-filters">
          <div className="filter-group">
            <label>Filter by Status:</label>
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="all">All Orders</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="orders-count">
            <span>{filteredOrders.length} orders found</span>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="orders-table-container desktop-table">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <div className="order-number">#{order.id}</div>
                  </td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">
                        {order.user.firstName} {order.user.lastName}
                      </div>
                      <div className="customer-email">{order.user.email}</div>
                    </div>
                  </td>
                  <td>
                    <div className="order-items">
                      <span className="items-count">{getOrderItems(order).length} items</span>
                      <div className="items-preview">
                        {getOrderItems(order).slice(0, 2).map((item) => (
                          <div key={item.id} className="item-preview">
                            {item.product?.name || 'Unknown Product'} (×{item.quantity || 0})
                          </div>
                        ))}
                        {getOrderItems(order).length > 2 && (
                          <div className="items-more">+{getOrderItems(order).length - 2} more</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="order-total">€{Number(order.totalAmount || 0).toFixed(2)}</div>
                  </td>
                  <td>
                    <select
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                      className={`status-select ${getStatusBadgeClass(order.status)}`}
                    >
                      <option value="PENDING">Pending</option>
                      <option value="CONFIRMED">Confirmed</option>
                      <option value="PROCESSING">Processing</option>
                      <option value="SHIPPED">Shipped</option>
                      <option value="DELIVERED">Delivered</option>
                      <option value="CANCELLED">Cancelled</option>
                    </select>
                  </td>
                  <td>
                    <span className={`payment-badge ${getPaymentStatusClass(order.paymentStatus || 'pending')}`}>
                      {order.paymentStatus === 'paid' ? 'Paid' :
                       order.paymentStatus === 'failed' ? 'Failed' :
                       'Pending'}
                    </span>
                  </td>
                  <td>
                    <div className="order-date">
                      {new Date(order.createdAt).toLocaleDateString('el-GR')}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-action btn-view"
                        onClick={() => handleViewOrder(order)}
                        title="View Details"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button 
                        className="btn-action btn-delete"
                        onClick={() => handleDeleteOrder(order.id)}
                        title="Delete Order"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6H5H21" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6H19Z" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="orders-cards-container mobile-cards">
          {filteredOrders.map((order) => (
            <div key={order.id} className="order-card">
              <div className="card-header">
                <div className="order-number-mobile">#{order.orderNumber}</div>
                <div className="order-date-mobile">
                  {new Date(order.createdAt).toLocaleDateString('el-GR')}
                </div>
              </div>
              
              <div className="card-content">
                <div className="card-detail">
                  <span className="detail-label">Customer:</span>
                  <div className="customer-info-mobile">
                    <div className="customer-name-mobile">
                      {order.user.firstName} {order.user.lastName}
                    </div>
                    <div className="customer-email-mobile">{order.user.email}</div>
                  </div>
                </div>
                
                <div className="card-detail">
                  <span className="detail-label">Items:</span>
                  <div className="order-items-mobile">
                    <span className="items-count-mobile">{getOrderItems(order).length} items</span>
                    <div className="items-preview-mobile">
                      {getOrderItems(order).slice(0, 2).map((item) => (
                        <div key={item.id} className="item-preview-mobile">
                          {item.product?.name || 'Unknown Product'} (×{item.quantity || 0})
                        </div>
                      ))}
                      {getOrderItems(order).length > 2 && (
                        <div className="items-more-mobile">+{getOrderItems(order).length - 2} more</div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="card-detail">
                  <span className="detail-label">Total:</span>
                  <div className="order-total-mobile">€{Number(order.totalAmount || 0).toFixed(2)}</div>
                </div>
                
                <div className="card-detail">
                  <span className="detail-label">Payment:</span>
                  <span className={`payment-badge ${getPaymentStatusClass(order.paymentStatus || 'pending')}`}>
                    {order.paymentStatus === 'paid' ? 'Paid' :
                     order.paymentStatus === 'failed' ? 'Failed' :
                     'Pending'}
                  </span>
                </div>
                
                <div className="card-detail">
                  <span className="detail-label">Status:</span>
                  <select
                    value={order.status}
                    onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                    className={`status-select-mobile ${getStatusBadgeClass(order.status)}`}
                  >
                    <option value="PENDING">Pending</option>
                    <option value="CONFIRMED">Confirmed</option>
                    <option value="PROCESSING">Processing</option>
                    <option value="SHIPPED">Shipped</option>
                    <option value="DELIVERED">Delivered</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
              </div>
              
              <div className="card-actions">
                <button 
                  className="btn-action btn-view-mobile"
                  onClick={() => handleViewOrder(order)}
                  title="View Details"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '6px'}}>
                    <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  View Details
                </button>
                <button 
                  className="btn-action btn-delete-mobile"
                  onClick={() => handleDeleteOrder(order.id)}
                  title="Delete Order"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '6px'}}>
                    <path d="M3 6H5H21" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6H19Z" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredOrders.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 8C21 8 21 8 21 8L12 13L3 8" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 8V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V8" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>No Orders Found</h3>
            <p>No orders match the current filter criteria</p>
          </div>
        )}

        {/* Order Details Modal */}
        {showOrderDetails && selectedOrder && (
          <OrderDetailsModal
            order={selectedOrder}
            onClose={() => {
              setShowOrderDetails(false);
              setSelectedOrder(null);
            }}
            onStatusUpdate={(orderId, status) => {
              updateOrderStatus(orderId, status);
              setShowOrderDetails(false);
              setSelectedOrder(null);
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

interface OrderDetailsModalProps {
  order: Order;
  onClose: () => void;
  onStatusUpdate: (orderId: number, status: string) => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ order, onClose, onStatusUpdate }) => {
  const [newStatus, setNewStatus] = useState<'PENDING' | 'CONFIRMED' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED'>(order.status);

  // Helper function to get order items regardless of API structure
  const getOrderItems = (order: Order): OrderItem[] => {
    return order.items || order.orderItems || [];
  };

  const handleStatusUpdate = () => {
    onStatusUpdate(order.id, newStatus);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content order-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Order Details #{order.orderNumber}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="order-details-content">
          <div className="details-grid">
            <div className="detail-section">
              <h3>Customer Information</h3>
              <div className="detail-info">
                <p><strong>Name:</strong> {order.user.firstName} {order.user.lastName}</p>
                <p><strong>Email:</strong> {order.user.email}</p>
              </div>
            </div>

            <div className="detail-section">
              <h3>Order Information</h3>
              <div className="detail-info">
                <p><strong>Order Date:</strong> {new Date(order.createdAt).toLocaleString('el-GR')}</p>
                <p><strong>Total Amount:</strong> €{Number(order.totalAmount || 0).toFixed(2)}</p>
                <p><strong>Payment Status:</strong> 
                  <span className={`payment-badge ${order.paymentStatus === 'paid' ? 'payment-paid' : 'payment-pending'}`}>
                    {order.paymentStatus}
                  </span>
                </p>
              </div>
            </div>

            <div className="detail-section full-width">
              <h3>Shipping Address</h3>
              <div className="detail-info">
                {order.shippingAddress ? (
                  <>
                    <p>{order.shippingAddress.street}</p>
                    <p>{order.shippingAddress.city}, {order.shippingAddress.postalCode}</p>
                    <p>{order.shippingAddress.country}</p>
                  </>
                ) : (
                  <p>No shipping address available</p>
                )}
              </div>
            </div>

            <div className="detail-section full-width">
              <h3>Order Items</h3>
              <div className="order-items-list">
                {getOrderItems(order).map((item) => (
                  <div key={item.id} className="order-item-detail">
                    <div className="item-image">
                      {item.product?.images ? (
                        <img 
                          src={proxyBucketUrl(item.product.images?.[0]) || '/products/placeholder.svg'} 
                          alt={item.product?.name || 'Product'}
                          onError={handleImageError}
                        />
                      ) : (
                        <div className="image-placeholder">📷</div>
                      )}
                    </div>
                    <div className="item-details">
                      <h4>{item.product?.name || 'Unknown Product'}</h4>
                      <p>Quantity: {item.quantity || 0}</p>
                      <p>Price: €{Number(item.price || 0).toFixed(2)} each</p>
                    </div>
                    <div className="item-total">
                      €{(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="detail-section full-width">
              <h3>Update Order Status</h3>
              <div className="status-update">
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as 'PENDING' | 'CONFIRMED' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED')}
                  className="status-select-modal"
                >
                  <option value="PENDING">Pending</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="SHIPPED">Shipped</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
                <button 
                  className="btn btn-primary"
                  onClick={handleStatusUpdate}
                  disabled={newStatus === order.status}
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrders;